import { computed, Injectable, signal } from '@angular/core';
import {
  AnalysisTemplate,
  CustomerSite,
  DailyEnergyResponse,
  DateRangeValue,
  EnergyItem,
  GroupAnalysisState,
  MdbPowerChartData,
  TemplateDetail,
} from './group-analysis.interface';
const today = new Date();
const initialState: GroupAnalysisState = {
  templates: [],
  selectedTemplate: null,
  templateDetail: null,

  dailyEnergyPieChart: [],
  monthlyEnergyPieChart: [],
  dailyCategories: [],
  dailySeries: [],
  dailyEnergyPeriod: 'month',
  startDate: new Date(today.getFullYear(), today.getMonth(), 1),
  endDate: new Date(today),
  customerSite: null,
  unit: 0,
  yesterdayEnergyPieChart: [],
  lastMonthlyEnergyPieChart: [],
  loadingTemplates: false,
  loadingDashboard: false,
  error: null,

  mdbPowerCategories: [],
  mdbPowerSeries: [],
};

@Injectable({ providedIn: 'root' })
export class GroupAnalysisStore {
  private readonly state = signal<GroupAnalysisState>(initialState);

  readonly templates = computed(() => this.state().templates);

  readonly selectedTemplate = computed(() => this.state().selectedTemplate);

  readonly templateDetail = computed(() => this.state().templateDetail);

  readonly dailyEnergyPieChart = computed(
    () => this.state().dailyEnergyPieChart,
  );
  readonly dailyEnergyPeriod = computed(() => this.state().dailyEnergyPeriod);

  readonly monthlyEnergyPieChart = computed(
    () => this.state().monthlyEnergyPieChart,
  );
  readonly mdbPowerCategories = computed(() => this.state().mdbPowerCategories);

  readonly mdbPowerSeries = computed(() => this.state().mdbPowerSeries);
  readonly totalYesterdayEnergyPieChart = computed(() =>
    this.state().yesterdayEnergyPieChart.reduce(
      (total, item) => total + Number(item.energy || 0),
      0,
    ),
  );

  readonly totalLastMonthlyEnergyPieChart = computed(() =>
    this.state().lastMonthlyEnergyPieChart.reduce(
      (total, item) => total + Number(item.energy || 0),
      0,
    ),
  );
  readonly dailyCategories = computed(() => this.state().dailyCategories);

  readonly dailySeries = computed(() => this.state().dailySeries);

  readonly startDate = computed(() => this.state().startDate);

  readonly endDate = computed(() => this.state().endDate);

  readonly unit = computed(() => this.state().unit);

  readonly loadingTemplates = computed(() => this.state().loadingTemplates);

  readonly loadingDashboard = computed(() => this.state().loadingDashboard);

  readonly loading = computed(
    () => this.state().loadingTemplates || this.state().loadingDashboard,
  );

  readonly error = computed(() => this.state().error);

  readonly totaldailyEnergyPieChart = computed(() =>
    this.state().dailyEnergyPieChart.reduce(
      (total, item) => total + Number(item.energy || 0),
      0,
    ),
  );
  readonly totalmonthlyEnergyPieChart = computed(() =>
    this.state().monthlyEnergyPieChart.reduce(
      (total, item) => total + Number(item.energy || 0),
      0,
    ),
  );
  readonly yesterdayEnergyPieChart = computed(
    () => this.state().yesterdayEnergyPieChart,
  );

  readonly lastMonthlyEnergyPieChart = computed(
    () => this.state().lastMonthlyEnergyPieChart,
  );

  readonly customerSite = computed(() => this.state().customerSite);
  setCustomerSite(customerSite: CustomerSite | null): void {
    this.patch({
      customerSite,
    });
  }
  setTemplates(templates: AnalysisTemplate[]): void {
    this.patch({
      templates,
    });
  }
    
  setMdbPower(data: MdbPowerChartData): void {
    this.patch({
      mdbPowerCategories: data.categories,
      mdbPowerSeries: data.series,
    });
  }
  setSelectedTemplate(template: AnalysisTemplate | null): void {
    this.patch({
      selectedTemplate: template,
    });
  }

  setComparisonEnergyItem(
    yesterday: EnergyItem[],
    lastMonth: EnergyItem[],
  ): void {
    this.patch({
      yesterdayEnergyPieChart: yesterday,
      lastMonthlyEnergyPieChart: lastMonth,
    });
  }
  setTemplateDetail(templateDetail: TemplateDetail | null): void {
    this.patch({ templateDetail });
  }

  setEnergyItem(
    dailyEnergyPieChart: EnergyItem[],
    monthlyEnergyPieChart: EnergyItem[],
  ): void {
    this.patch({ dailyEnergyPieChart });
    this.patch({ monthlyEnergyPieChart });
  }

  setDailyEnergyPeriod(period: 'week' | 'month'): void {
    this.patch({
      dailyEnergyPeriod: period,
    });
  }

  setDailyEnergy(data: DailyEnergyResponse): void {
    this.patch({
      dailyCategories: data.categories ?? [],
      dailySeries: data.series ?? [],
    });
  }

  setDateRange(range: DateRangeValue): void {
    this.patch({
      startDate: range.start,
      endDate: range.end,
    });
  }

  setLoadingTemplate(value: boolean): void {
    this.patch({
      loadingTemplates: value,
    });
  }

  setLoadingDashboard(value: boolean): void {
    this.patch({
      loadingDashboard: value,
    });
  }

  setError(error: string | null): void {
    this.patch({
      error,
    });
  }

  setUnit(unit: number): void {
    this.patch({
      unit,
    });
  }

  // clearDashboard(): void {
  //   this.patch({
  //     templateDetail: null,
  //     energyItems: [],
  //     dailyCategories: [],
  //     dailySeries: [],
  //   });
  // }

  reset(): void {
    this.state.set(initialState);
  }

  private patch(partialState: Partial<GroupAnalysisState>): void {
    this.state.update((currentState) => ({
      ...currentState,
      ...partialState,
    }));
  }
}
